function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useRef, useState } from 'react';
import { convertRichTextToHTML, Drawer, FullColumn, isEmptyRichTextString, useOverlayContext } from 'seagull';
import { useAcceptServiceDescription } from "../../../hooks/reactQuery/mutations/useAcceptServiceDescription";
import { useGenerateServiceDescription } from "../../../hooks/reactQuery/mutations/useGenerateServiceDescription";
import { GenerateServiceDescriptionType } from "../../../hooks/reactQuery/mutations/useGenerateServiceDescription.types";
import { useRejectServiceDescription } from "../../../hooks/reactQuery/mutations/useRejectServiceDescription";
import { useUndoServiceDescription } from "../../../hooks/reactQuery/mutations/useUndoServiceDescription";
import { useIoContext } from "../../../providers/IoContext";
import { useStoreActions } from "../../../store/store-context";
import DrawerHeader from "./DrawerHeader";
import FeedbackModal from "./FeedbackModal/FeedbackModal";
import styles from "./HelpMeWriteDrawer.module.scss";
import { emptyResponseData, getResponseData, GetHelpMeWriteDrawerTID } from "./HelpMeWriteDrawer.utils";
import HelpMeWriteTextField from "./HelpMeWriteTextField/HelpMeWriteTextField";
import SuggestedDescriptionDisplay from "./SuggestedDescriptionDisplay/SuggestedDescriptionDisplay";
import { DESCRIPTION_PROMPT } from "./SuggestedDescriptionDisplay/SuggestedDescriptionDisplay.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const HelpMeWriteDrawer = _ref => {
  let {
    open,
    onClose,
    drawerWidth = 564,
    serviceName,
    serviceDescription,
    serviceId,
    onInsert
  } = _ref;
  const {
    HelpMeWriteDrawerTID
  } = useMemo(() => GetHelpMeWriteDrawerTID(), []);
  const overlayContext = useOverlayContext();
  const {
    logBI
  } = useStoreActions();
  const {
    intercomClient
  } = useIoContext();
  const [textPrompt, setTextPrompt] = useState('');
  const [response, setResponse] = useState(emptyResponseData);
  const {
    generateDescription,
    isLoading: isGenerateDescriptionLoading
  } = useGenerateServiceDescription();
  const {
    undoDescription,
    isLoading: isUndoLoading
  } = useUndoServiceDescription();
  const {
    acceptDescription
  } = useAcceptServiceDescription();
  const {
    rejectDescription
  } = useRejectServiceDescription();
  const hasTriggeredRef = useRef(false);
  const hasUsedSuggestionRef = useRef(false);
  const initialHasServiceDescriptionRef = useRef(null);
  const [isInserting, setIsInserting] = useState(false);
  const {
    canUndo,
    isClarification,
    conversationId,
    description,
    isEmpty
  } = response;
  const {
    hasServiceDescription,
    htmlDescription
  } = useMemo(() => ({
    hasServiceDescription: !isEmptyRichTextString(serviceDescription),
    htmlDescription: convertRichTextToHTML(serviceDescription)
  }), [serviceDescription]);
  const handleClose = () => {
    onClose();
    setTextPrompt('');
    setResponse(emptyResponseData);
    hasUsedSuggestionRef.current = false;
    initialHasServiceDescriptionRef.current = null;
    setIsInserting(false);
  };
  const closeAndReject = () => {
    const shouldShowFeedback = !isEmpty && !hasUsedSuggestionRef.current;
    logBI(17011);
    handleClose();
    if (!isEmpty) {
      rejectDescription(conversationId);
    }
    if (shouldShowFeedback) {
      overlayContext.showModal({
        closeOnClickOutside: true,
        closeOnEscapeKey: true,
        content: _ref2 => {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/_jsx(FeedbackModal, {
            conversationId: conversationId,
            onClose: dismissModal
          });
        }
      });
    }
  };
  useEffect(() => {
    if (open) {
      intercomClient.hideIntercom();
    } else {
      intercomClient.showIntercom();
    }
  }, [open, intercomClient]);
  useEffect(() => {
    if (!open) {
      hasTriggeredRef.current = false;
      setResponse(emptyResponseData);
      setTextPrompt('');
      initialHasServiceDescriptionRef.current = null;
      setIsInserting(false);
      return;
    }

    // Only check hasServiceDescription when drawer first opens
    if (initialHasServiceDescriptionRef.current === null) {
      initialHasServiceDescriptionRef.current = hasServiceDescription;
      if (hasServiceDescription) {
        setResponse(_objectSpread(_objectSpread({}, emptyResponseData), {}, {
          description: DESCRIPTION_PROMPT
        }));
        return;
      }
    }
    if (serviceName !== null && serviceName !== void 0 && serviceName.trim() && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      generateDescription({
        requestType: GenerateServiceDescriptionType.Title,
        title: serviceName,
        serviceId
      }).then(result => setResponse(getResponseData(result)));
    }
  }, [open, serviceName, serviceId, generateDescription, hasServiceDescription]);
  const handleGenerateDescription = async request => {
    const result = await generateDescription(request);
    setResponse(getResponseData(result));
  };
  const handleSubmit = async () => {
    if (!textPrompt.trim()) return;
    if (isEmpty) {
      await handleGenerateDescription({
        requestType: GenerateServiceDescriptionType.Description,
        description: htmlDescription,
        message: textPrompt,
        title: serviceName,
        type: 'message',
        serviceId
      });
      return;
    }
    await handleGenerateDescription({
      requestType: GenerateServiceDescriptionType.Message,
      message: textPrompt,
      conversationId: response.conversationId,
      type: 'message',
      serviceId
    });
  };
  const handlePresetClick = async preset => {
    if (isEmpty) {
      await handleGenerateDescription({
        requestType: GenerateServiceDescriptionType.Description,
        description: htmlDescription,
        preset,
        title: serviceName,
        type: 'preset',
        serviceId
      });
      return;
    }
    await handleGenerateDescription({
      requestType: GenerateServiceDescriptionType.Preset,
      preset,
      conversationId,
      type: 'preset',
      serviceId
    });
  };
  const handleInsert = parsedRichText => {
    hasUsedSuggestionRef.current = true;
    setIsInserting(true);
    onInsert === null || onInsert === void 0 || onInsert(parsedRichText);
    setTimeout(() => handleClose(), 2000);
  };
  const handleAccept = conversationId => {
    acceptDescription(conversationId);
  };
  const handleUndo = async () => {
    if (!conversationId) return;
    const response = await undoDescription(conversationId);
    setResponse(getResponseData(response));
  };
  return /*#__PURE__*/_jsx(Drawer, {
    variant: "persistent",
    minWidth: drawerWidth - 4,
    open: open,
    onClose: closeAndReject,
    onCloseButtonClick: closeAndReject,
    disableCloseButton: isInserting,
    PaperProps: _objectSpread(_objectSpread({}, {
      'data-testid': HelpMeWriteDrawerTID.Paper()
    }), {}, {
      className: styles.drawerPaper,
      style: {
        width: drawerWidth
      }
    }),
    showDividers: false,
    header: /*#__PURE__*/_jsx(DrawerHeader, {}),
    testId: HelpMeWriteDrawerTID.Root(),
    children: /*#__PURE__*/_jsxs(FullColumn, {
      gap: 16,
      padding: 16,
      justifyContent: "space-between",
      testId: HelpMeWriteDrawerTID.Content(),
      children: [/*#__PURE__*/_jsx(SuggestedDescriptionDisplay, {
        hasServiceDescription: hasServiceDescription,
        isLoading: isGenerateDescriptionLoading,
        isUndoLoading: isUndoLoading,
        suggestedDescription: description,
        canUndo: canUndo,
        conversationId: conversationId,
        isClarification: isClarification,
        onInsert: handleInsert,
        onUndo: handleUndo,
        onAccept: handleAccept,
        isInserting: isInserting
      }), /*#__PURE__*/_jsx(HelpMeWriteTextField, {
        conversationId: conversationId,
        value: textPrompt,
        onChange: setTextPrompt,
        onSubmit: handleSubmit,
        onPresetClick: handlePresetClick,
        isLoading: isGenerateDescriptionLoading || !description,
        disabled: isGenerateDescriptionLoading || !description || isInserting,
        isUndoLoading: isUndoLoading
      })]
    })
  });
};
export default HelpMeWriteDrawer;